/**
 * 财务初审工作台
 *
 * 这个页面存在的意义本身就是一条业务约束：**自动化校验再完善，也必须有人点这一下**。
 * 因此页面上没有任何「全部通过」按钮，批量操作只提供「批量退回」，
 * 通过必须逐张打开详情、看过票面与校验报告后人工确认。
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { AuditOutlined, ReloadOutlined, SafetyOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  INVOICE_TYPE_LABELS,
  InvoiceStatus,
  InvoiceTypeCode,
  PermissionCode,
  RISK_LEVEL_LABELS,
  RiskLevel,
  formatCents,
  summarizeRiskLevel,
  type InvoiceListItem,
} from '@ilc/shared';
import { invoiceApi } from '@/api/endpoints';
import { mockInvoiceList } from '@/mocks';
import { usePagedList } from '@/hooks/usePagedList';
import { useAuthStore } from '@/stores/auth';
import { DataTable, BatchBar } from '@/components/DataTable';
import { PageHeader } from '@/components/PageHeader';
import { buildInvoiceColumns, buildInvoiceExportColumns } from '@/components/invoiceColumns';
import { ConfirmDanger } from '@/components/ConfirmDanger';
import { performAction } from '@/utils/performAction';
import { feedback } from '@/utils/feedback';

interface Query {
  page?: number;
  pageSize?: number;
  keyword?: string;
  riskLevel?: RiskLevel;
  invoiceType?: InvoiceTypeCode;
  dateFrom?: string;
  dateTo?: string;
}

export default function ReviewerWorkbench(): React.ReactElement {
  const navigate = useNavigate();
  const auth = useAuthStore((s) => s.auth);
  const can = useAuthStore((s) => s.can);
  const canApproveAmount = useAuthStore((s) => s.canApproveAmount);
  const canReleaseRisk = useAuthStore((s) => s.canReleaseRisk);

  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [rejectTargets, setRejectTargets] = useState<InvoiceListItem[] | null>(null);
  const [busy, setBusy] = useState(false);

  const list = usePagedList<InvoiceListItem, Query>({
    loader: (q) => invoiceApi.list({ ...q, status: InvoiceStatus.PENDING_REVIEW }),
    fallback: (q) => mockInvoiceList({ ...q, status: InvoiceStatus.PENDING_REVIEW }),
    initialQuery: { page: 1, pageSize: 20 },
  });

  const stats = useMemo(() => {
    const rows = list.list;
    return {
      total: list.total,
      high: rows.filter((r) => r.riskLevel === RiskLevel.HIGH).length,
      overLimit: rows.filter((r) => !canApproveAmount(r.totalCents ?? 0)).length,
      overRisk: rows.filter((r) => !canReleaseRisk(summarizeRiskLevel(r.riskItems ?? []))).length,
    };
  }, [list.list, list.total, canApproveAmount, canReleaseRisk]);

  const handleBatchReject = async (reason: string): Promise<void> => {
    if (!rejectTargets || rejectTargets.length === 0) return;
    setBusy(true);
    let ok = 0;
    for (const target of rejectTargets) {
      const result = await performAction({
        action: () => invoiceApi.reject(target.id, { reason }),
        successMessage: `已退回 ${target.bizNo}`,
        offline: () => undefined,
        offlineMessage: '后端退回接口尚未就绪，本次退回仅在本地演示，未通知经办人',
      });
      if (result.ok) ok += 1;
    }
    setBusy(false);
    setRejectTargets(null);
    setSelectedKeys([]);
    if (ok > 0) {
      feedback.info(`共处理 ${ok} 张发票`);
      list.reload();
    }
  };

  const columns = buildInvoiceColumns({
    showHandler: true,
    showVoucher: false,
    actions: (record) => (
      <>
        <Button type="link" size="small" onClick={() => navigate(`/business/invoices/${record.id}`)}>
          审阅并处理
        </Button>
        <Button type="link" size="small" onClick={() => navigate(`/business/invoices/${record.id}/report`)}>
          校验报告
        </Button>
      </>
    ),
  });

  const selectedRows = list.list.filter((r) => selectedKeys.includes(r.id));

  return (
    <div className="ilc-page">
      <PageHeader
        title="财务初审"
        description={`待初审发票按风险等级排序处理。当前账号可审核金额上限 ${
          auth?.amountLimitCents === null || auth?.amountLimitCents === undefined
            ? '不限'
            : `¥${formatCents(auth.amountLimitCents)}`
        }，可放行风险等级最高为 ${auth?.maxRiskLevel ? RISK_LEVEL_LABELS[auth.maxRiskLevel] : '不限'}。`}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={list.reload}>
              刷新
            </Button>
            <Button
              icon={<SafetyOutlined />}
              onClick={() => {
                list.setQuery({ riskLevel: RiskLevel.HIGH });
              }}
            >
              只看高风险
            </Button>
          </Space>
        }
      />

      <Alert
        type="info"
        showIcon
        icon={<AuditOutlined />}
        style={{ marginBottom: 12 }}
        message="人工初审不可跳过"
        description="OCR、企查查、税务局、合同比对的结果只是给您的参考材料：系统不会因为「校验通过」就自动放行，也不会因为「校验不通过」就自动作废。通过、退回、特例放行三个动作都需要您本人在发票详情页操作并留痕；批量操作中只提供「批量退回」，避免把审批变成一路回车。"
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic title="待初审" value={stats.total} suffix="张" />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title="本页高风险"
              value={stats.high}
              suffix="张"
              valueStyle={{ color: '#cf1322' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title="超出我的审核金额上限"
              value={stats.overLimit}
              suffix="张"
              valueStyle={{ color: '#d46b08' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title="超出我的可放行风险等级"
              value={stats.overRisk}
              suffix="张"
              valueStyle={{ color: '#d46b08' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {(stats.overLimit > 0 || stats.overRisk > 0) && list.list.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="本页存在超出您权限范围的发票"
          description={`其中 ${stats.overLimit} 张金额超出您的审核上限、${stats.overRisk} 张风险等级超出您可放行范围。这些发票在详情页的「通过」按钮会被禁用，需由更高权限的财务负责人处理或走特例放行流程。`}
        />
      ) : null}

      <DataTable<InvoiceListItem>
        storageKey="reviewer-invoices"
        exportFileName="待初审发票"
        exportColumns={buildInvoiceExportColumns()}
        columns={columns}
        dataSource={list.list}
        rowKey="id"
        loading={list.loading}
        error={list.error}
        isFallback={list.isFallback}
        fallbackReason={list.fallbackReason}
        onRetry={list.reload}
        pagination={list.pagination}
        onChange={list.handleTableChange}
        emptyText="当前没有待初审的发票，校验完成的发票会自动出现在这里"
        rowSelection={{ selectedRowKeys: selectedKeys, onChange: (keys) => setSelectedKeys(keys) }}
        footer={() =>
          selectedKeys.length > 0 ? (
            <BatchBar count={selectedKeys.length} onClear={() => setSelectedKeys([])}>
              <Button danger size="small" onClick={() => setRejectTargets(selectedRows)}>
                批量退回经办人
              </Button>
              <Tooltip title="通过必须逐张审阅：批量通过会把人工审核变成走过场">
                <Button size="small" disabled>
                  批量通过（不提供）
                </Button>
              </Tooltip>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                退回需填写原因，会通知上传人
              </Typography.Text>
            </BatchBar>
          ) : null
        }
        toolbar={
          <Space wrap>
            <Input.Search
              placeholder="内部单号 / 发票号码 / 销方名称"
              allowClear
              style={{ width: 260 }}
              onSearch={(value) => list.setQuery({ keyword: value })}
            />
            <Select
              placeholder="风险等级"
              allowClear
              style={{ width: 130 }}
              value={list.query.riskLevel}
              onChange={(value) => list.setQuery({ riskLevel: value })}
              options={Object.entries(RISK_LEVEL_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Select
              placeholder="票种"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: 200 }}
              value={list.query.invoiceType}
              onChange={(value) => list.setQuery({ invoiceType: value })}
              options={Object.entries(INVOICE_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <DatePicker.RangePicker
              style={{ width: 240 }}
              onChange={(range) =>
                list.setQuery({
                  dateFrom: range?.[0] ? dayjs(range[0]).format('YYYYMMDD') : undefined,
                  dateTo: range?.[1] ? dayjs(range[1]).format('YYYYMMDD') : undefined,
                })
              }
            />
            <Button onClick={list.resetQuery}>重置</Button>
          </Space>
        }
      />

      <Card size="small" title="初审判断口径" style={{ marginTop: 16 }}>
        <ul style={{ margin: 0, paddingInlineStart: 18, lineHeight: 2, fontSize: 13 }}>
          <li>
            <Tag color="red">高风险</Tag> 红冲、作废、供应商注销/吊销：<strong>默认阻断</strong>，确需入账走特例放行，
            必须填写专项审批依据（由具备「特例放行」权限的人员操作）。
          </li>
          <li>
            <Tag color="orange">中风险</Tag> 超经营范围、合同金额/品类不一致、票面与税务记录不一致：
            <strong>只提示不拦截</strong>，由您判断业务真实性后决定是否继续，判断理由请在退回原因或审批意见中写明。
          </li>
          <li>
            <Tag color="blue">低风险</Tag> OCR 置信度低、字段缺失、未关联合同：多为数据质量问题，
            可要求经办人修正后重新提交。
          </li>
          <li>
            <Tag color="orange">外部校验暂不可用</Tag>
            接口异常时校验项会被标记为「暂不可用」，需要 <strong>人工核验并接管</strong>（填写核验说明）后业务才能继续，
            不会卡死流程。核验说明是事后抽查的主要依据。
          </li>
        </ul>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          初审通过后发票进入「待推送 T+」；推送只把业务数据放进 T+ 待办业务池，
          <strong>凭证仍需财务在 T+ 端选择会计科目生成</strong>，本平台不生成凭证。
          详情页可直达：<Link to="/business/invoices">我的发票</Link>（如需按单号定位）。
        </Typography.Paragraph>
      </Card>

      <ConfirmDanger
        open={rejectTargets !== null}
        title={`批量退回 ${rejectTargets?.length ?? 0} 张发票`}
        level="normal"
        confirmText="确认退回"
        loading={busy}
        description={
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <span>以下发票将退回给各自的上传经办人，需修改后重新提交：</span>
            <ul style={{ margin: 0, paddingInlineStart: 18, maxHeight: 160, overflow: 'auto' }}>
              {(rejectTargets ?? []).slice(0, 20).map((t) => (
                <li key={t.id}>
                  <span className="ilc-mono">{t.bizNo}</span> · {t.sellerName ?? '-'} · ¥
                  {formatCents(t.totalCents ?? 0)}
                </li>
              ))}
            </ul>
            {(rejectTargets?.length ?? 0) > 20 ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                （仅列出前 20 条）
              </Typography.Text>
            ) : null}
          </Space>
        }
        reasonLabel="统一退回原因（必填，不少于 8 字）"
        reasonPresets={[
          '票面信息与原件不一致，请核对后重新上传',
          '发票金额与合同约定不符，请补充说明或调整',
          '缺少必要的合同关联，请先关联合同再提交',
          '税号识别有误，请修正后再提交初审',
        ]}
        onConfirm={(reason) => handleBatchReject(reason)}
        onCancel={() => setRejectTargets(null)}
      />

      {!can(PermissionCode.INVOICE_REVIEW) ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          message="当前账号没有「财务初审」权限"
          description="您可以查看待初审列表，但无法在详情页执行通过 / 退回操作。如需参与初审请联系租户管理员分配角色。"
        />
      ) : null}
    </div>
  );
}
